import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import { photoFillImageTransform } from '@/lib/photos/photo-fill-transform'
import {
  getCurrentPhotoFillCoveredSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'

export interface PhotoTintLayout {
  /** The shape's own outline, punched out of the tint mask. */
  shapePathD: string
  tintImageWidth: number
  tintImageHeight: number
  tintImageTransform: string | undefined
  tintMaskId: string
}

/**
 * Full photo + outside-path tint: while cropping, show the photo's FULL rendered extent —
 * including whatever continues past the shape's own geometric frame (cover-fit always sizes the
 * photo to be at least as large as the frame, and panning/zooming can push it further past the
 * frame's edges) — not just what the path/frame clips in, dimmed with the same
 * white-wash-at-75%-opacity tint the dim overlay already uses elsewhere in crop mode.
 *
 * Applies to EVERY photo-filled shape, not just non-rectangular ones — a plain rectangle's own
 * clip is its geometric frame exactly, but the photo drawn inside it can still extend well past
 * that frame (any zoom above native cover-fit, or a natural aspect ratio that doesn't exactly
 * match the frame's on one axis) and get clipped away today just the same as a star or custom
 * path clips its corners. `shapePathD` resolves the shape's own outline to punch out of the
 * tint: prefers `pathCommands` (kept in sync on every resize by `applyPartialShapeUpdate`),
 * then the raw `path` string, then — for a shape with neither (a plain rectangle never gets
 * one; also covers the rare in-session preset shape never round-tripped through a save/reload)
 * — a synthesized plain rectangle spanning the frame exactly, matching what shape-renderer/components/shape-photo-fill.tsx
 * actually clips to in that case.
 */
export function resolvePhotoTintLayout(
  shape: ShapeData,
  photoFill: ShapePhotoFill,
  width: number,
  height: number
): PhotoTintLayout {
  const shapePathD =
    Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0
      ? pathCommandsToSvgPath(shape.pathCommands, { close: true })
      : typeof shape.path === 'string' && shape.path.trim().length > 0
        ? shape.path
        : `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`
  const { naturalWidth: tintNaturalWidth, naturalHeight: tintNaturalHeight } =
    resolveEffectivePhotoFillNaturalSize(photoFill)
  const tintCoveredSize = getCurrentPhotoFillCoveredSize(
    width,
    height,
    tintNaturalWidth,
    tintNaturalHeight,
    Math.max(photoFill.scale, 1)
  )
  const tintImageWidth = tintCoveredSize?.coveredWidth ?? width
  const tintImageHeight = tintCoveredSize?.coveredHeight ?? height
  // Same transform shape-renderer/components/shape-photo-fill.tsx applies to the real (path-clipped) image — reused here
  // verbatim (not re-derived) so the full/tinted copy lines up with it exactly.
  const tintImageTransform = photoFillImageTransform({
    frameWidth: width,
    frameHeight: height,
    offsetX: photoFill.offsetX,
    offsetY: photoFill.offsetY,
    scale: Math.max(photoFill.scale, 1),
    coveredWidth: tintCoveredSize?.coveredWidth,
    coveredHeight: tintCoveredSize?.coveredHeight,
    flippedX: photoFill.flippedX,
    flippedY: photoFill.flippedY,
    rotation: photoFill.rotation,
  })
  const tintMaskId = `crop-photo-tint-mask-${shape.id}`

  return { shapePathD, tintImageWidth, tintImageHeight, tintImageTransform, tintMaskId }
}
